import area from "@turf/area";
import bbox from "@turf/bbox";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import centroid from "@turf/centroid";
import distance from "@turf/distance";
import { point } from "@turf/helpers";
import rhumbDestination from "@turf/rhumb-destination";
import { createSeededRandomNumberGenerator } from "@iventis/utilities";
import GeoJSON, { Feature, Point } from "geojson";
import { normalisePolygonCoordinates, SupportedPolygon } from "./normalise-coordinate";

const MAX_ATTEMPTS = 5;
const METRES_PER_KILOMETRE = 1000;
const RANDOM_POINT_ATTEMPT_MULTIPLIER = 100;
const MIN_RANDOM_POINT_ATTEMPTS = 10000;

/**
 * Generates evenly spaced point features inside a polygon by placing a square grid over the polygon's bounding box.
 *
 * The grid is sized from the polygon area and target count, then adjusted a few times so the number of grid centres
 * that fall inside the polygon is close to the requested count. The returned points are deterministic for the same
 * polygon and target count.
 *
 * @param inputPolygon Polygon or multipolygon to place points within.
 * @param targetPointCount Maximum number of points to return.
 * @returns Up to targetPointCount grid-centre points contained by the polygon.
 */
export function generateUniformGridPointsWithinPolygon(inputPolygon: SupportedPolygon, targetPointCount: number): Feature<Point>[] {
    if (targetPointCount === 0) {
        return [];
    }

    if (targetPointCount === 1) {
        return [centroid(inputPolygon)];
    }

    let attempt = 0;
    let actualPointCount = 0;
    let bestPointCount = 0;

    // Turf area is in square metres, so this cell size is also in metres.
    let cellSize = Math.sqrt(area(inputPolygon) / targetPointCount);
    let bestCellSize = cellSize;
    const upperLimit = Math.ceil(targetPointCount * 1.1);

    while (attempt < MAX_ATTEMPTS) {
        const validPoints = generateUniformGridPointCentresWithinPolygon(inputPolygon, cellSize);
        actualPointCount = validPoints.length;

        // Track the closest grid that produces enough points, even if it is above the preferred 10% tolerance.
        if (actualPointCount >= targetPointCount) {
            if (bestPointCount < targetPointCount || actualPointCount < bestPointCount) {
                bestPointCount = actualPointCount;
                bestCellSize = cellSize;
            }
        }

        if (actualPointCount >= targetPointCount && actualPointCount <= upperLimit) {
            bestCellSize = cellSize;
            break;
        }

        // If too few centres are inside the polygon, shrink the cells. If too many are inside, enlarge them.
        const adjustmentFactor = Math.sqrt(targetPointCount / (actualPointCount || 1));
        cellSize /= adjustmentFactor;
        attempt += 1;
    }

    return generateUniformGridPointCentresWithinPolygon(inputPolygon, bestCellSize).slice(0, targetPointCount);
}

/**
 * Generates deterministic pseudo-random point features inside a polygon.
 *
 * The polygon coordinates and target count are converted into the initial state of a pure-rand generator. Calls with the
 * same polygon and target count return the same points, which is useful when random-looking placement needs to be
 * repeatable.
 *
 * @param inputPolygon Polygon or multipolygon to place points within.
 * @param targetPointCount Maximum number of points to return.
 * @returns Up to targetPointCount seeded random points contained by the polygon.
 */
export function generateSeededRandomPointsWithinPolygon(inputPolygon: SupportedPolygon, targetPointCount: number): Feature<Point>[] {
    if (targetPointCount === 0) {
        return [];
    }

    const normalisedPolygon = normalisePolygonCoordinates(inputPolygon);
    const [west, south, east, north] = bbox(normalisedPolygon);
    const random = createSeededRandomNumberGenerator(createPolygonAndQuantitySeed(normalisedPolygon, targetPointCount));
    const validPoints: Feature<Point>[] = [];
    const maxAttempts = getRandomPointAttemptLimit(normalisedPolygon, targetPointCount, [west, south, east, north]);
    let attempt = 0;

    while (validPoints.length < targetPointCount && attempt < maxAttempts) {
        // Rejection sample from the bounding box: cheap to generate, then filtered by polygon containment.
        const longitude = west + random() * (east - west);
        const latitude = south + random() * (north - south);
        const candidatePoint = point([longitude, latitude]);

        if (booleanPointInPolygon(candidatePoint, normalisedPolygon)) {
            validPoints.push(candidatePoint);
        }

        attempt += 1;
    }

    return validPoints;
}

function createPolygonAndQuantitySeed(inputPolygon: SupportedPolygon, targetPointCount: number): string {
    const geometry = inputPolygon.type === "Feature" ? inputPolygon.geometry : inputPolygon;

    return `${targetPointCount}:${JSON.stringify(geometry.coordinates)}`;
}

/**
 * Creates centre points for a square grid and returns only the centres that are inside the polygon.
 *
 * @param inputPolygon Polygon or multipolygon used for containment filtering.
 * @param cellSizeInMetres Size of each grid square in metres.
 * @returns All grid-centre points contained by the polygon.
 */
function generateUniformGridPointCentresWithinPolygon(inputPolygon: SupportedPolygon, cellSizeInMetres: number): Feature<Point>[] {
    if (cellSizeInMetres <= 0) {
        return [];
    }

    const [west, south, east, north] = bbox(inputPolygon);
    const cellSizeInKilometres = cellSizeInMetres / METRES_PER_KILOMETRE;

    // Work in real-world distances rather than treating longitude/latitude degrees as uniform linear units.
    const widthInKilometres = distance([west, south], [east, south], { units: "kilometers" });
    const heightInKilometres = distance([west, south], [west, north], { units: "kilometers" });
    const columnCount = Math.ceil(widthInKilometres / cellSizeInKilometres);
    const rowCount = Math.ceil(heightInKilometres / cellSizeInKilometres);
    const validPoints: Feature<Point>[] = [];

    for (let row = 0; row < rowCount; row += 1) {
        // Move north first to find the centre line for this row, then east for each column centre.
        const rowCentre = rhumbDestination([west, south], (row + 0.5) * cellSizeInKilometres, 0, { units: "kilometers" });

        for (let column = 0; column < columnCount; column += 1) {
            const squareCentre = rhumbDestination(rowCentre, (column + 0.5) * cellSizeInKilometres, 90, { units: "kilometers" });
            const centrePoint = point(squareCentre.geometry.coordinates as GeoJSON.Position);

            if (booleanPointInPolygon(centrePoint, inputPolygon)) {
                validPoints.push(centrePoint);
            }
        }
    }

    return validPoints;
}

/**
 * Calculates a bounded number of random samples to try before returning the points found so far.
 *
 * Thin or fragmented polygons can occupy only a small part of their bounding box, so the cap scales with the ratio
 * between the bounding box area and the polygon area.
 *
 * @param inputPolygon Polygon or multipolygon being sampled.
 * @param targetPointCount Requested point count.
 * @param boundingBox Bounding box for the input polygon.
 * @returns Maximum number of random candidates to test.
 */
function getRandomPointAttemptLimit(inputPolygon: SupportedPolygon, targetPointCount: number, boundingBox: [number, number, number, number]): number {
    const [west, south, east, north] = boundingBox;
    const polygonArea = area(inputPolygon);
    const boundingBoxWidth = distance([west, south], [east, south], { units: "kilometers" }) * METRES_PER_KILOMETRE;
    const boundingBoxHeight = distance([west, south], [west, north], { units: "kilometers" }) * METRES_PER_KILOMETRE;
    const boundingBoxArea = boundingBoxWidth * boundingBoxHeight;
    const boundingBoxToPolygonRatio = polygonArea > 0 ? Math.max(1, Math.ceil(boundingBoxArea / polygonArea)) : 1;

    return Math.max(MIN_RANDOM_POINT_ATTEMPTS, targetPointCount * RANDOM_POINT_ATTEMPT_MULTIPLIER * boundingBoxToPolygonRatio);
}
